import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken, clearToken, getToken } from "@/lib/crmApi";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const IMPERSONATION_ORIGIN_KEY = "prospee_impersonation_origin";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading

  // Fiche complète de l'utilisateur connecté : organisation, permissions,
  // `platform_admin`… La réponse de /auth/login n'en porte qu'un extrait.
  const fetchMe = async () => {
    try { const { data } = await api.get("/auth/me"); return data; } catch { return null; }
  };

  const loadMe = useCallback(async () => {
    if (!getToken()) { setUser(null); return; }
    setUser(await fetchMe());
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    // Sans ce rechargement, les écrans réservés à l'administrateur de
    // plateforme (Corbeille, suppression d'une fiche) restaient cachés jusqu'au
    // premier F5 : la fiche de connexion ne portait pas le drapeau.
    const me = (await fetchMe()) || data.user;
    setUser(me);
    return me;
  };

  const logout = () => { clearToken(); setUser(null); window.location.href = "/connexion"; };

  // Emprunt d'identité (admin) : garde le token admin de côté (sessionStorage — jamais
  // persisté au-delà de l'onglet) pour pouvoir revenir sans se reconnecter.
  const impersonate = async (userId) => {
    const originalToken = getToken();
    const { data } = await api.post(`/auth/impersonate/${userId}`);
    sessionStorage.setItem(IMPERSONATION_ORIGIN_KEY, originalToken);
    setToken(data.token);
    setUser((await fetchMe()) || data.user);
  };

  const stopImpersonation = async () => {
    const originalToken = sessionStorage.getItem(IMPERSONATION_ORIGIN_KEY);
    if (!originalToken) return;
    try { await api.post("/auth/impersonate/stop"); } catch { /* audit best-effort */ }
    sessionStorage.removeItem(IMPERSONATION_ORIGIN_KEY);
    setToken(originalToken);
    await loadMe();
  };

  return (
    <AuthCtx.Provider value={{ user, login, logout, reload: loadMe, impersonate, stopImpersonation }}>
      {children}
    </AuthCtx.Provider>
  );
}
